import * as Sentry from '@sentry/react';
import { Capacitor } from '@capacitor/core';

/**
 * Traza del arranque — para un fallo que no deja rastro.
 *
 * ── Por qué hace falta ─────────────────────────────────────────────────────
 *
 * El arranque puede quedarse colgado sin lanzar nada: una promesa del puente
 * de Capacitor o de IndexedDB que no resuelve ni rechaza. No hay excepción, no
 * hay petición en rojo y `index.html` es un `<div id="root">` vacío, así que
 * lo único que ve el operario es una pantalla BLANCA. Sentry tampoco recibe
 * nada: sin evento no se envían breadcrumbs.
 *
 * Y cuando ocurre en carretera, no hay nadie con un cable USB delante.
 *
 * ── Cómo se recoge la evidencia ────────────────────────────────────────────
 *
 * Cada hito se escribe en `localStorage` de forma SÍNCRONA. Síncrona es la
 * palabra importante: una escritura asíncrona es justo lo que no cuaja cuando
 * el proceso se queda parado. Como `localStorage` sobrevive al cierre de la
 * app, el arranque SIGUIENTE puede leer dónde se quedó el anterior y
 * denunciarlo. El diagnóstico llega aunque la tablet no volviera a tener red
 * hasta el día siguiente.
 *
 * ── Sin gate de entorno, a propósito ───────────────────────────────────────
 *
 * Nada de `import.meta.env.DEV`: la APK de campo es un APK debug con un bundle
 * de PRODUCCIÓN, así que ese gate apaga la traza exactamente en el único sitio
 * donde hace falta. El coste se justifica por frecuencia — son ~8 líneas por
 * arranque, no un bucle— y `console.info` se ve en `logcat` sin más.
 */

/**
 * Hitos del arranque, en orden. El último escrito dice dónde se quedó.
 *
 * Los que tienen nombre de paso (`native-storage:session`, `apollo:seed`…) los
 * escribe `withDeadline` SOLO si el paso terminó: si agotó su plazo, va a la
 * lista de vencidos y el último hito sigue siendo el anterior. Así el rastro no
 * puede leerse como «terminó» cuando no terminó.
 */
export type BootMilestone =
  | 'script-evaluated'
  | 'updater-notified'
  | 'native-storage:start'
  | 'native-storage:persistence'
  | 'native-storage:session'
  | 'native-storage:drafts'
  | 'mirror-installed'
  | 'offline-session'
  | 'react-mounted'
  | 'apollo:seed'
  /**
   * La app es USABLE: Apollo listo y la primera pantalla de verdad en camino.
   * Montar React no basta —el splash también está montado—, y dar el arranque
   * por bueno ahí escondía justo el cuelgue que viene después.
   */
  | 'app-ready'
  /** Montado por la red de seguridad: el arranque NO terminó por su cuenta. */
  | 'rescue';

const LAST_MILESTONE_KEY = 'rois.boot.lastMilestone';
const STARTED_AT_KEY = 'rois.boot.startedAt';
/** Se pone cuando la app es usable y se borra al empezar. Si aparece, el arranque acabó. */
const COMPLETED_KEY = 'rois.boot.completed';
/**
 * Pasos que agotaron su plazo (o fallaron) en este arranque, acumulados.
 *
 * Va en clave APARTE y no dentro del último hito porque el hito lo pisa el paso
 * siguiente de inmediato: escribirlo ahí dejaba el rastro vivo unos
 * microsegundos y el post-mortem no veía nada.
 */
const EXPIRED_KEY = 'rois.boot.expired';
/** Dónde estaba el arranque cuando saltó la red de seguridad. */
const RESCUED_FROM_KEY = 'rois.boot.rescuedFrom';

/**
 * Si montó la red de seguridad. A partir de ese momento el último hito se
 * CONGELA: el arranque colgado puede reanudarse más tarde y sus hitos pisarían
 * la evidencia de dónde se quedó.
 */
let rescued = false;

function writeSync(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Un `localStorage` lleno o bloqueado no puede impedir arrancar. La traza
    // es diagnóstico: si se pierde, se pierde el diagnóstico, no la app.
  }
}

function readSync(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function platform(): 'native' | 'web' {
  return Capacitor.isNativePlatform() ? 'native' : 'web';
}

/** Apunta que un paso agotó su plazo o falló. Se acumulan: pueden caer varios. */
export function markExpired(step: BootMilestone, reason?: 'error'): void {
  const previous = readSync(EXPIRED_KEY);
  const entry = reason ? `${step}:${reason}` : step;
  writeSync(EXPIRED_KEY, previous ? `${previous},${entry}` : entry);
}

/** Pasos que vencieron en el arranque en curso. Vacío si fue todo bien. */
export function expiredSteps(): string {
  return readSync(EXPIRED_KEY) ?? '';
}

/**
 * Apunta un hito. Síncrono y barato: es lo que hace que sirva de algo.
 *
 * Sin `Sentry.addBreadcrumb` a propósito: el `console.info` ya lo convierte en
 * miga la integración de consola de Sentry, y las dos juntas duplicaban cada
 * hito en cualquier incidente.
 */
export function markBoot(milestone: BootMilestone): void {
  const startedAt = Number(readSync(STARTED_AT_KEY)) || Date.now();
  const elapsedMs = Date.now() - startedAt;

  if (!rescued) writeSync(LAST_MILESTONE_KEY, `${milestone}@${elapsedMs}`);

  // Sin gate de entorno: se lee en `logcat` con la APK de campo.
  console.info(`[boot] ${milestone} · ${elapsedMs} ms`);
}

/**
 * Abre un arranque y DENUNCIA el anterior si se quedó a medias.
 *
 * Aquí está el valor de todo el módulo: el arranque que se cuelga no puede
 * contar nada —está colgado—, así que lo cuenta el siguiente. Si al empezar
 * encontramos un arranque previo sin cerrar, sabemos que aquella vez la app no
 * llegó a ser usable, y en qué hito exacto se quedó.
 *
 * Se llama lo antes posible, antes de cualquier `await`.
 */
export function beginBoot(): void {
  const previousMilestone = readSync(LAST_MILESTONE_KEY);
  const previousCompleted = readSync(COMPLETED_KEY);

  // Solo en la app nativa. En web, `localStorage` lo comparten las pestañas:
  // abrir una segunda mientras la primera arranca dejaría el flag a medias y se
  // emitiría un `error` de Sentry por algo que no ha fallado. En el WebView no
  // hay pestañas concurrentes, así que ahí el flag sí significa lo que dice.
  if (Capacitor.isNativePlatform() && previousMilestone && previousCompleted !== '1') {
    // Falso positivo posible: que alguien cierre la app en el segundo que
    // tarda un arranque sano. Es una ventana pequeña y esto es diagnóstico, no
    // una alarma; si algún día hace ruido, se filtra por el `elapsedMs` que ya
    // viaja dentro del hito.
    console.warn(
      `[boot] the PREVIOUS boot did not finish — it stopped at ${previousMilestone}`
    );
    Sentry.captureMessage('[boot] previous boot hung', {
      level: 'error',
      tags: { feature: 'boot', platform: 'native' },
      extra: {
        lastMilestone: previousMilestone,
        expired: readSync(EXPIRED_KEY),
        rescuedFrom: readSync(RESCUED_FROM_KEY),
      },
    });
  }

  writeSync(STARTED_AT_KEY, String(Date.now()));
  writeSync(COMPLETED_KEY, '0');
  // Todo esto es de ESTE arranque: arrastrar lo del anterior haría creer que
  // algo falla cuando ya se resolvió.
  writeSync(EXPIRED_KEY, '');
  writeSync(RESCUED_FROM_KEY, '');
  markBoot('script-evaluated');
}

/**
 * Cierra el arranque: la app es USABLE. Mientras no se llame, el arranque
 * siguiente lo dará por colgado.
 *
 * Si montó la red de seguridad NO se marca como terminado, aunque la app acabe
 * funcionando: el arranque no terminó por su cuenta, y marcarlo borraría la
 * única evidencia que queda —la que lee el siguiente— justo en el caso para el
 * que existe todo esto.
 */
export function completeBoot(): void {
  markBoot('app-ready');
  if (!rescued) writeSync(COMPLETED_KEY, '1');
}

/**
 * La red de seguridad ha tenido que montar la app. Se apunta DÓNDE estaba el
 * arranque y a partir de aquí el último hito queda congelado.
 */
export function markRescued(): void {
  writeSync(RESCUED_FROM_KEY, lastMilestone());
  markBoot('rescue');
  rescued = true;
}

/** El último hito del arranque en curso. */
export function lastMilestone(): string {
  return readSync(LAST_MILESTONE_KEY) ?? 'unknown';
}

/**
 * Corre una tarea con plazo. Si vence, devuelve `fallback` y SIGUE.
 *
 * 🔴 El detalle que hace que esto no sea un `try/catch` más: un `catch` atrapa
 * el rechazo, y el fallo que nos ocupa no rechaza — no contesta. La única
 * defensa contra «no contesta» es el reloj.
 *
 * 🔴 Y el reloj NO cancela la tarea: ni el puente de Capacitor ni IndexedDB
 * exponen cancelación. Lo que se abandona sigue vivo y puede volver más tarde
 * —con la app ya en marcha— y escribir encima de lo que pasó mientras: la sesión
 * de un operario sobre la del siguiente, sus borradores, su caché de Apollo.
 * Por eso la tarea recibe `isAbandoned()`, que pasa a `true` en el instante en
 * que vence el plazo, y NO debe escribir nada una vez abandonada. Es mejor que
 * pedirle a cada tarea que re-compruebe el estado a mano tras cada `await`: eso
 * ya se olvidó una vez, y la tarea que se envuelva mañana lo olvidaría otra.
 *
 * Si termina a tiempo se apunta su hito; si vence o falla, va a la lista de
 * vencidos y el último hito sigue siendo el anterior.
 */
export async function withDeadline<T>(
  task: (isAbandoned: () => boolean) => Promise<T>,
  ms: number,
  fallback: T,
  step: BootMilestone
): Promise<T> {
  let abandoned = false;
  const isAbandoned = (): boolean => abandoned;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const expiry = new Promise<{ expired: true }>((resolve) => {
    timer = setTimeout(() => {
      // En el propio temporizador y no después del `await`: cualquier
      // continuación de la tarea que corra a partir de aquí ya lo ve.
      abandoned = true;
      resolve({ expired: true });
    }, ms);
  });

  try {
    const outcome = await Promise.race([
      task(isAbandoned).then((value) => ({ expired: false as const, value })),
      expiry,
    ]);

    if (outcome.expired) {
      markExpired(step);
      console.warn(`[boot] ${step} exceeded its ${ms} ms deadline — continuing without it`);
      Sentry.captureMessage(`[boot] ${step} deadline exceeded`, {
        level: 'warning',
        tags: { feature: 'boot', platform: platform() },
        extra: { deadlineMs: ms },
      });
      return fallback;
    }
    markBoot(step);
    return outcome.value;
  } catch (error) {
    // Un rechazo normal tampoco para el arranque, pero sí tiene que llegar a
    // Sentry como incidencia: antes lo hacía el `try/catch` de `main.tsx`, que
    // desde que existe este envoltorio ya no ve ningún rechazo.
    abandoned = true;
    markExpired(step, 'error');
    console.warn(`[boot] ${step} failed — continuing without it`, error);
    Sentry.captureException(error, {
      tags: { feature: 'boot', platform: platform(), step },
    });
    return fallback;
  } finally {
    clearTimeout(timer);
  }
}
